import {
  extractImagePathsFromRecordImagesField,
  wirePathToImageDisplaySrc,
} from './assetImageResolve';
import {
  extractProfileAvailableCredits,
  extractProfileDateOfBirth,
  extractProfileOccupation,
  profileImageFromRecord,
} from './profileDisplaySummary';
import {
  fallbackWorkerDisplayName,
  isBusinessWorkerProfileRecord,
  isOwnerLogoImageUrl,
  meaningfulWorkerField,
  shouldUseWorkerPersonalNameFields,
  workerRowEchoesEmployerContact,
} from './profileWorkerIdentity';
import {
  extractProfileRecordList,
  isInactiveWorker,
  resolveWorkerRecordId,
} from './profileWorkerList';
import {
  loadWorkerListAvatarSrc,
  persistWorkerListAvatarSrc,
} from './workerListAvatarStorage';
import { loadWorkerAvatarByEmail, persistWorkerAvatarByEmail } from './workerAvatarByEmailStorage';

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function pickString(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return null;
}

/** Nested envelopes that may hold the employee profile on GET /profile/workers rows. */
const WORKER_IDENTITY_NESTED_KEYS = [
  'worker',
  'business_worker',
  'businessWorker',
  'user',
  'profile',
  'data',
  'result',
] as const;

/** Employer-only nested envelopes — never use for worker name/photo. */
const EMPLOYER_NESTED_KEYS = ['business_client', 'businessClient', 'client'] as const;

/** Flat business-client fields echoed on worker rows — never use for worker list identity. */
const BUSINESS_CLIENT_ECHO_KEYS = [
  'business_client_id',
  'business_name',
  'business_type',
  'business_client',
  'businessClient',
  'client',
  'contact_first_name',
  'contact_last_name',
  'contactFirstName',
  'contactLastName',
  'logo',
  'logo_url',
  'logoUrl',
  'brand_logo',
  'brandLogo',
  'business_logo',
] as const;

const WORKER_IMAGE_KEYS = [
  'profile_image',
  'profileImage',
  'profile_image_url',
  'profileImageUrl',
  'profile_picture',
  'profilePicture',
  'avatar',
  'avatar_url',
  'avatarUrl',
  'image',
  'image_url',
  'imageUrl',
  'photo',
  'photo_url',
  'picture',
  'picture_url',
] as const;

/** Flat GET /profile/workers keys for a worker photo — never `logo` / `logo_url` (employer branding). */
const FLAT_WORKER_PHOTO_KEYS = WORKER_IMAGE_KEYS;

/** Worker identity from the API — use even when stored under owner-logos paths. */
const EXPLICIT_WORKER_PROFILE_IMAGE_KEYS = [
  'profile_image',
  'profileImage',
  'profile_image_url',
  'profileImageUrl',
  'profile_picture',
  'profilePicture',
] as const;

export type BusinessClientWorkerListItem = {
  id: string;
  displayName: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  phoneSecondary: string | null;
  nationalId: string | null;
  birthDate: string | null;
  gender: string | null;
  position: string | null;
  occupation: string | null;
  addressLabel: string | null;
  status: string | null;
  avatarSrc: string | null;
  availableCredits: number | null;
  isInactive: boolean;
};

function nestedSourcesForKeys(
  record: Record<string, unknown>,
  keys: readonly string[],
): Record<string, unknown>[] {
  const sources: Record<string, unknown>[] = [];
  for (const key of keys) {
    const nested = record[key];
    if (isPlainObject(nested)) {
      sources.push(nested);
    }
  }
  return sources;
}

function workerIdentitySources(record: Record<string, unknown>): Record<string, unknown>[] {
  return nestedSourcesForKeys(record, WORKER_IDENTITY_NESTED_KEYS);
}

function readNestedString(
  sources: Record<string, unknown>[],
  read: (source: Record<string, unknown>) => string | null,
): string | null {
  for (const source of sources) {
    const value = read(source);
    if (value) {
      return value;
    }
  }
  return null;
}

function normalizeBusinessId(value: string | null | undefined): string {
  return value?.trim().toLowerCase() ?? '';
}

export type BusinessClientEmployerContext = {
  businessIds: string[];
  businessClientIds: string[];
  emails: string[];
  contactFirst: string | null;
  contactLast: string | null;
};

function uniqueNormalizedIds(values: (string | null | undefined)[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    const trimmed = value?.trim();
    if (!trimmed) {
      continue;
    }
    const key = normalizeBusinessId(trimmed);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    result.push(trimmed);
  }
  return result;
}

/** Employer keys used to match GET /profile/workers rows (business_id is often the login email). */
export function buildBusinessClientEmployerContext(
  profileRecord: Record<string, unknown> | null,
  options?: {
    businessId?: string | null;
    contactFirst?: string | null;
    contactLast?: string | null;
    email?: string | null;
    loginIdentifier?: string | null;
  },
): BusinessClientEmployerContext {
  const businessIds: string[] = [];
  const businessClientIds: string[] = [];
  const emails: string[] = [];

  const pushBusinessId = (value: string | null | undefined) => {
    const trimmed = value?.trim();
    if (trimmed) {
      businessIds.push(trimmed);
    }
  };
  const pushBusinessClientId = (value: string | null | undefined) => {
    const trimmed = value?.trim();
    if (trimmed) {
      businessClientIds.push(trimmed);
    }
  };
  const pushEmail = (value: string | null | undefined) => {
    const trimmed = value?.trim();
    if (trimmed) {
      emails.push(trimmed);
    }
  };

  pushBusinessId(options?.businessId);
  pushEmail(options?.email);
  pushEmail(options?.loginIdentifier);

  if (profileRecord) {
    pushBusinessId(workerBusinessIdFromRecord(profileRecord));
    pushBusinessClientId(
      pickString(profileRecord.business_client_id, profileRecord.businessClientId),
    );
    pushEmail(pickString(profileRecord.email, profileRecord.Email));
    for (const key of EMPLOYER_NESTED_KEYS) {
      const nested = profileRecord[key];
      if (!isPlainObject(nested)) {
        continue;
      }
      pushBusinessId(pickString(nested.business_id, nested.businessId));
      pushBusinessClientId(
        pickString(
          nested.business_client_id,
          nested.businessClientId,
          nested.client_id,
          nested.clientId,
          nested.id,
        ),
      );
      pushEmail(pickString(nested.email, nested.Email));
    }
  }

  return {
    businessIds: uniqueNormalizedIds(businessIds),
    businessClientIds: uniqueNormalizedIds(businessClientIds),
    emails: uniqueNormalizedIds(emails),
    contactFirst: meaningfulWorkerField(options?.contactFirst) ?? null,
    contactLast: meaningfulWorkerField(options?.contactLast) ?? null,
  };
}

function normalizeEmployerContext(
  businessIdOrEmployer?: string | null | BusinessClientEmployerContext,
): BusinessClientEmployerContext | null {
  if (!businessIdOrEmployer) {
    return null;
  }
  if (typeof businessIdOrEmployer === 'string') {
    const trimmed = businessIdOrEmployer.trim();
    return trimmed
      ? {
          businessIds: [trimmed],
          businessClientIds: [],
          emails: [],
          contactFirst: null,
          contactLast: null,
        }
      : null;
  }
  return businessIdOrEmployer;
}

function employerContextHasMatchKeys(employer: BusinessClientEmployerContext): boolean {
  return (
    employer.businessIds.length > 0 ||
    employer.businessClientIds.length > 0 ||
    employer.emails.length > 0
  );
}

function isUsableWorkerImageUrl(url: string | null | undefined): boolean {
  const trimmed = url?.trim();
  if (!trimmed || isOwnerLogoImageUrl(trimmed)) {
    return false;
  }
  return true;
}

function meaningfulZipCode(value: string | null | undefined): string | null {
  const trimmed = meaningfulWorkerField(value);
  if (!trimmed) {
    return null;
  }
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length > 0 && /^0+$/.test(digits)) {
    return null;
  }
  return trimmed;
}

export function workerBusinessIdFromRecord(record: Record<string, unknown>): string | null {
  return pickString(record.business_id, record.businessId);
}

/** Keep only workers whose employer keys match the signed-in business client. */
export function workerRecordMatchesBusinessId(
  record: Record<string, unknown>,
  businessIdOrEmployer?: string | null | BusinessClientEmployerContext,
): boolean {
  const employer = normalizeEmployerContext(businessIdOrEmployer);
  if (!employer || !employerContextHasMatchKeys(employer)) {
    return true;
  }

  const workerBusinessId = workerBusinessIdFromRecord(record);
  const workerBusinessClientId = pickString(
    record.business_client_id,
    record.businessClientId,
  );

  if (workerBusinessId) {
    const normalizedWorkerBusinessId = normalizeBusinessId(workerBusinessId);
    for (const businessId of employer.businessIds) {
      if (normalizedWorkerBusinessId === normalizeBusinessId(businessId)) {
        return true;
      }
    }
    for (const email of employer.emails) {
      if (normalizedWorkerBusinessId === normalizeBusinessId(email)) {
        return true;
      }
    }
  }

  if (workerBusinessClientId) {
    const normalizedWorkerClientId = normalizeBusinessId(workerBusinessClientId);
    for (const businessClientId of employer.businessClientIds) {
      if (normalizedWorkerClientId === normalizeBusinessId(businessClientId)) {
        return true;
      }
    }
  }

  return false;
}

function normalizePersonName(value: string | null | undefined): string {
  return value?.trim().toLowerCase() ?? '';
}

function businessClientNestedContactNames(
  record: Record<string, unknown>,
): { first: string | null; last: string | null } {
  for (const key of EMPLOYER_NESTED_KEYS) {
    const nested = record[key];
    if (!isPlainObject(nested)) {
      continue;
    }
    const first = pickString(
      nested.contact_first_name,
      nested.contactFirstName,
      nested.first_name,
      nested.firstName,
    );
    const last = pickString(
      nested.contact_last_name,
      nested.contactLastName,
      nested.last_name,
      nested.lastName,
    );
    if (first || last) {
      return { first, last };
    }
  }
  return { first: null, last: null };
}

function workerNameIsBusinessClientEcho(
  record: Record<string, unknown>,
  firstName: string | null,
  lastName: string | null,
  employer?: BusinessClientEmployerContext | null,
): boolean {
  if (workerRowEchoesEmployerContact(record)) {
    return true;
  }
  const nestedContact = businessClientNestedContactNames(record);
  if (firstName && lastName && nestedContact.first && nestedContact.last) {
    if (
      normalizePersonName(firstName) === normalizePersonName(nestedContact.first) &&
      normalizePersonName(lastName) === normalizePersonName(nestedContact.last)
    ) {
      return true;
    }
  }
  if (firstName && lastName && employer?.contactFirst && employer?.contactLast) {
    return (
      normalizePersonName(firstName) === normalizePersonName(employer.contactFirst) &&
      normalizePersonName(lastName) === normalizePersonName(employer.contactLast)
    );
  }
  return false;
}

function workerEmailFromRecord(
  record: Record<string, unknown>,
  identitySources: Record<string, unknown>[],
): string | null {
  const email =
    readNestedString(identitySources, (source) => pickString(source.email, source.Email)) ??
    pickString(record.email, record.Email);
  const businessId = pickString(record.business_id, record.businessId);
  if (
    email &&
    businessId &&
    normalizeBusinessId(email) === normalizeBusinessId(businessId)
  ) {
    return null;
  }
  return email;
}

function coerceImageUrlString(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }
  if (isPlainObject(value)) {
    return pickString(value.url, value.src, value.href, value.path, value.uri, value.link);
  }
  return null;
}

function rowEmployerLogoUrl(record: Record<string, unknown>): string | null {
  return pickString(record.logo, record.logo_url, record.logoUrl);
}

function imageUrlEchoesRowEmployerLogo(
  record: Record<string, unknown>,
  imageUrl: string | null | undefined,
): boolean {
  const logo = rowEmployerLogoUrl(record);
  const image = imageUrl?.trim();
  if (!logo || !image) {
    return false;
  }
  return normalizeBusinessId(logo) === normalizeBusinessId(image);
}

function readExplicitWorkerProfileImage(record: Record<string, unknown>): string | null {
  for (const key of EXPLICIT_WORKER_PROFILE_IMAGE_KEYS) {
    const raw = coerceImageUrlString(record[key]);
    if (!raw || imageUrlEchoesRowEmployerLogo(record, raw)) {
      continue;
    }
    return raw;
  }
  return null;
}

function workerPhotoFromImagesField(record: Record<string, unknown>): string | null {
  const paths = extractImagePathsFromRecordImagesField(record);
  if (!paths?.length) {
    return null;
  }
  for (const path of paths) {
    if (imageUrlEchoesRowEmployerLogo(record, path)) {
      continue;
    }
    if (isUsableWorkerImageUrl(path)) {
      return path;
    }
    if (!isOwnerLogoImageUrl(path)) {
      return path;
    }
  }
  return null;
}

function workerPhotoFromFlatRecord(record: Record<string, unknown>): string | null {
  for (const key of FLAT_WORKER_PHOTO_KEYS) {
    if ((EXPLICIT_WORKER_PROFILE_IMAGE_KEYS as readonly string[]).includes(key)) {
      continue;
    }
    const raw = coerceImageUrlString(record[key]);
    if (raw && isUsableWorkerImageUrl(raw) && !imageUrlEchoesRowEmployerLogo(record, raw)) {
      return raw;
    }
  }
  return null;
}

function workerProfileImageFromSources(
  record: Record<string, unknown>,
  identitySources: Record<string, unknown>[],
): string | null {
  const fromExplicitFlat = readExplicitWorkerProfileImage(record);
  if (fromExplicitFlat) {
    return fromExplicitFlat;
  }
  const fromImagesField = workerPhotoFromImagesField(record);
  if (fromImagesField) {
    return fromImagesField;
  }
  for (const source of identitySources) {
    const fromExplicitNested = readExplicitWorkerProfileImage(source);
    if (fromExplicitNested) {
      return fromExplicitNested;
    }
    const nestedImages = workerPhotoFromImagesField(source);
    if (nestedImages) {
      return nestedImages;
    }
  }
  const fromFlat = workerPhotoFromFlatRecord(record);
  if (fromFlat) {
    return fromFlat;
  }
  const fromFlatLegacy = profileImageFromRecord(record);
  if (
    fromFlatLegacy &&
    isUsableWorkerImageUrl(fromFlatLegacy) &&
    !imageUrlEchoesRowEmployerLogo(record, fromFlatLegacy)
  ) {
    return fromFlatLegacy;
  }
  for (const source of identitySources) {
    const fromNestedFlat = workerPhotoFromFlatRecord(source);
    if (fromNestedFlat) {
      return fromNestedFlat;
    }
    for (const key of WORKER_IMAGE_KEYS) {
      const raw = coerceImageUrlString(source[key]);
      if (raw && isUsableWorkerImageUrl(raw) && !imageUrlEchoesRowEmployerLogo(record, raw)) {
        return raw;
      }
    }
    const nestedImage = profileImageFromRecord(source);
    if (
      nestedImage &&
      isUsableWorkerImageUrl(nestedImage) &&
      !imageUrlEchoesRowEmployerLogo(record, nestedImage)
    ) {
      return nestedImage;
    }
  }
  return null;
}

function stripBusinessClientEchoFields(record: Record<string, unknown>): void {
  for (const key of BUSINESS_CLIENT_ECHO_KEYS) {
    delete record[key];
  }
}

/**
 * Worker-only view of a GET /profile/workers row (no business-client contact/logo fields).
 */
export function resolveWorkerListItemRecord(
  record: Record<string, unknown>,
  employer?: BusinessClientEmployerContext | null,
): Record<string, unknown> {
  const identitySources = workerIdentitySources(record);

  const nestedFirst = readNestedString(identitySources, (source) =>
    pickString(source.first_name, source.firstName),
  );
  const nestedLast = readNestedString(identitySources, (source) =>
    pickString(source.last_name, source.lastName),
  );
  const flatFirst = pickString(record.first_name, record.firstName);
  const flatLast = pickString(record.last_name, record.lastName);

  let firstName = nestedFirst;
  let lastName = nestedLast;
  if (!firstName && !lastName) {
    firstName = flatFirst;
    lastName = flatLast;
  }

  if (workerNameIsBusinessClientEcho(record, firstName, lastName, employer)) {
    firstName = null;
    lastName = null;
  }

  const email = workerEmailFromRecord(record, identitySources);
  const phone =
    readNestedString(identitySources, (source) =>
      pickString(source.phone_number, source.phoneNumber, source.phone, source.mobile),
    ) ?? pickString(record.phone_number, record.phoneNumber, record.phone, record.mobile);

  const echoesEmployer = workerRowEchoesEmployerContact(record);
  const merged: Record<string, unknown> = { ...record };

  if (firstName) {
    merged.first_name = firstName;
  } else {
    delete merged.first_name;
  }
  if (lastName) {
    merged.last_name = lastName;
  } else {
    delete merged.last_name;
  }
  if (email) {
    merged.email = email;
  }
  if (phone) {
    merged.phone_number = phone;
  }

  const profileImage = workerProfileImageFromSources(record, identitySources);
  if (profileImage) {
    merged.profile_image = profileImage;
  } else {
    delete merged.profile_image;
  }

  if (echoesEmployer) {
    delete merged.phone_number_2;
  }

  stripBusinessClientEchoFields(merged);

  return merged;
}

export function extractWorkerFirstName(record: Record<string, unknown>): string | null {
  return meaningfulWorkerField(pickString(record.first_name, record.firstName));
}

export function extractWorkerLastName(record: Record<string, unknown>): string | null {
  return meaningfulWorkerField(pickString(record.last_name, record.lastName));
}

export function workerAvatarFromRecord(
  record: Record<string, unknown>,
  identitySources: Record<string, unknown>[] = workerIdentitySources(record),
): string | null {
  const profileImage = workerProfileImageFromSources(record, identitySources);
  if (!profileImage) {
    return null;
  }
  return wirePathToImageDisplaySrc(profileImage) ?? profileImage;
}

export function resolveWorkerListAvatarSrc(
  record: Record<string, unknown>,
  workerId: string,
): string | null {
  const id = workerId.trim();
  if (!id) {
    return null;
  }
  const email = pickString(record.email, record.Email);
  const fromApi = workerAvatarFromRecord(record);
  if (fromApi) {
    persistWorkerListAvatarSrc(id, fromApi);
    if (email) {
      persistWorkerAvatarByEmail(email, fromApi);
    }
    return fromApi;
  }
  const cached = loadWorkerListAvatarSrc(id);
  if (cached) {
    return wirePathToImageDisplaySrc(cached) ?? cached;
  }
  const fromEmail = loadWorkerAvatarByEmail(email);
  if (fromEmail) {
    return wirePathToImageDisplaySrc(fromEmail) ?? fromEmail;
  }
  return null;
}

export function formatWorkerAddressLabel(address: unknown): string | null {
  if (!isPlainObject(address)) {
    return null;
  }
  const parts = [
    meaningfulWorkerField(pickString(address.street, address.line1)),
    meaningfulWorkerField(pickString(address.address2, address.line2, address.address_2)),
    meaningfulWorkerField(pickString(address.city)),
    meaningfulZipCode(pickString(address.zip_code, address.postalCode, address.zipCode)),
  ].filter((part): part is string => Boolean(part));
  return parts.length ? parts.join(', ') : null;
}

export function workerListPrimaryLabel(worker: BusinessClientWorkerListItem): string {
  const hasRealName = Boolean(worker.firstName?.trim() || worker.lastName?.trim());
  if (hasRealName) {
    return worker.displayName;
  }
  return worker.email?.trim() || worker.displayName;
}

export function workerListSecondaryLabel(worker: BusinessClientWorkerListItem): string | null {
  const hasRealName = Boolean(worker.firstName?.trim() || worker.lastName?.trim());
  if (hasRealName) {
    return worker.email?.trim() || null;
  }
  return worker.position?.trim() || worker.phone?.trim() || worker.nationalId?.trim() || null;
}

export function workerDisplayNameFromRecord(
  record: Record<string, unknown>,
  employer?: BusinessClientEmployerContext | null,
): string {
  const resolved = resolveWorkerListItemRecord(record, employer);
  const joined = [extractWorkerFirstName(resolved), extractWorkerLastName(resolved)]
    .filter(Boolean)
    .join(' ')
    .trim();
  if (joined) {
    return joined;
  }
  const email = pickString(resolved.email, resolved.Email);
  if (email) {
    return email;
  }
  return fallbackWorkerDisplayName(email);
}

export function businessClientWorkerFromRecord(
  record: Record<string, unknown>,
  employer?: BusinessClientEmployerContext | null,
): BusinessClientWorkerListItem | null {
  const id = resolveWorkerRecordId(record);
  if (!id) {
    return null;
  }
  const resolved = resolveWorkerListItemRecord(record, employer);
  const email = pickString(resolved.email, resolved.Email);
  return {
    id,
    displayName: workerDisplayNameFromRecord(record, employer),
    firstName: extractWorkerFirstName(resolved),
    lastName: extractWorkerLastName(resolved),
    email,
    phone: pickString(resolved.phone_number, resolved.phoneNumber, resolved.phone, resolved.mobile),
    phoneSecondary: meaningfulWorkerField(
      pickString(resolved.phone_number_2, resolved.phoneNumber2, resolved.secondary_phone),
    ),
    nationalId: meaningfulWorkerField(
      pickString(resolved.national_id, resolved.nationalId, resolved.id_number),
    ),
    birthDate: extractProfileDateOfBirth(resolved),
    gender: meaningfulWorkerField(pickString(resolved.gender)),
    position: meaningfulWorkerField(pickString(resolved.position)),
    occupation: meaningfulWorkerField(extractProfileOccupation(resolved)),
    addressLabel: formatWorkerAddressLabel(resolved.address),
    status: meaningfulWorkerField(pickString(resolved.status)),
    avatarSrc: resolveWorkerListAvatarSrc(record, id),
    availableCredits: extractProfileAvailableCredits(resolved),
    isInactive: isInactiveWorker(record),
  };
}

/**
 * List workers for a business client: match `business_id`, show worker name/email/photo only.
 */
export function parseBusinessClientWorkersList(
  data: unknown,
  businessIdOrEmployer?: string | null | BusinessClientEmployerContext,
): BusinessClientWorkerListItem[] {
  const employer = normalizeEmployerContext(businessIdOrEmployer);
  return extractProfileRecordList(data)
    .filter((record) => workerRecordMatchesBusinessId(record, employer))
    .map((record) => businessClientWorkerFromRecord(record, employer))
    .filter((worker): worker is BusinessClientWorkerListItem => worker !== null)
    .sort((left, right) => left.displayName.localeCompare(right.displayName));
}

export const WORKER_DISPLAY_EMPLOYER_NESTED_KEYS = EMPLOYER_NESTED_KEYS;

export {
  fallbackWorkerDisplayName,
  isBusinessWorkerProfileRecord,
  isOwnerLogoImageUrl,
  meaningfulWorkerField,
  shouldUseWorkerPersonalNameFields,
  workerRowEchoesEmployerContact,
} from './profileWorkerIdentity';
